import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Sparkles, ArrowRight, ArrowLeft, Briefcase, Baby, CheckCircle2, Plus, X } from "lucide-react";

/**
 * OnboardingWizard — 3 step dopo la prima registrazione o al primo accesso
 * se onboarding_completed non è true nel profilo utente.
 *
 * Step 1: Come lavori? (dipendente / P.IVA / entrambi / nessuno)
 * Step 2: Dettagli del profilo di lavoro (dipendente e/o P.IVA)
 * Step 3: Famiglia (figli + ISEE)
 */
export default function OnboardingWizard({ open, onClose }) {
  const { realUser, setUser } = useAuth();
  const [step, setStep] = useState(1);
  // Fix URGENTE: stato INTERNO che permette al wizard di chiudersi da solo
  // al click su X/Salta/ESC/Interact outside, senza dipendere dal parent che flippi `open`.
  // Se il parent riapre il wizard (nuova sessione), il useEffect resetta questo flag.
  const [internallyClosed, setInternallyClosed] = useState(false);

  // Quando il parent riapre il wizard (open passa da false a true), resettiamo lo stato interno.
  useEffect(() => {
    if (open) {
      setInternallyClosed(false);
      setStep(1);
    }
  }, [open]);

  // Chiusura INCONDIZIONATA al click: aggiorna stato locale + notifica parent.
  // Se il parent NON aggiorna `open` per qualsiasi motivo, la modale resta chiusa
  // comunque perché `effectiveOpen` guarda anche `internallyClosed`.
  const closeNow = () => {
    setInternallyClosed(true);
    try { onClose?.(); } catch { /* non deve mai bloccare la chiusura */ }
  };

  const effectiveOpen = open && !internallyClosed;

  // Fix M7-M8: prefill workType da profilo esistente (se già Registrazione ha catturato info)
  const initialWorkType = (() => {
    const hasDip = !!realUser?.tipo_lavoro || !!realUser?.fascia_ral || !!realUser?.ral_lordo;
    const hasPiva = (realUser?.tipo_piva && realUser.tipo_piva !== "nessuna") || !!realUser?.codice_ateco;
    if (hasDip && hasPiva) return "entrambi";
    if (hasDip) return "dipendente";
    if (hasPiva) return "piva";
    return "";
  })();

  // Step 1: tipo lavoro
  const [workType, setWorkType] = useState(initialWorkType);

  // Step 2: dettagli lavoro
  const [ral, setRal] = useState(realUser?.ral_lordo || "");
  const [mensilita, setMensilita] = useState(13);
  const [tipoPiva, setTipoPiva] = useState("forfettario");
  const [ateco, setAteco] = useState(realUser?.codice_ateco || "");

  // Step 3: famiglia
  const [figli, setFigli] = useState([]);
  const [isee, setIsee] = useState("");

  const isDipendente = workType === "dipendente" || workType === "entrambi";
  const isPiva = workType === "piva" || workType === "entrambi";

  const totalSteps = workType === "nessuno" ? 1 : 3;

  const finish = async (skipRemaining = false) => {
    // Fix URGENTE: chiudi la modale SUBITO e INCONDIZIONATAMENTE sullo stato locale.
    // La PATCH parte in background — se fallisce, mostra un toast ma la modale
    // resta chiusa comunque.
    closeNow();
    // Salva in background senza bloccare la UI
    const payload = { onboarding_completed: true };
    if (workType && workType !== "nessuno") {
      payload.has_dipendente_income = isDipendente;
      if (isDipendente && ral) {
        payload.ral_lordo = parseFloat(ral) || null;
        payload.mensilita = parseInt(mensilita) || 13;
        payload.tipo_contratto = "indeterminato";
      }
      if (isPiva) {
        payload.tipo_piva = tipoPiva;
        if (ateco) payload.codice_ateco = ateco;
      } else if (workType === "dipendente") {
        payload.tipo_piva = "nessuna";
      }
      if (!skipRemaining) {
        if (figli.length > 0) payload.figli_a_carico = figli.map((f) => ({ eta: parseInt(f.eta) || 0, disabilita: null }));
        if (isee) payload.isee = parseFloat(isee) || null;
      }
    }
    try {
      const r = await api.patch("/auth/profile", payload);
      setUser(r.data);
      toast.success("Profilo configurato! Benvenuto in TUOTAX 🎉");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio profilo. Puoi completare i dati da Impostazioni.");
    }
  };

  const next = () => {
    if (step === 1 && !workType) {
      toast.error("Scegli come lavori per continuare");
      return;
    }
    if (step === 1 && workType === "nessuno") {
      finish();
      return;
    }
    setStep(step + 1);
  };

  return (
    <Dialog open={effectiveOpen} onOpenChange={(v) => { if (!v) closeNow(); }} modal={true}>
      <DialogContent
        className="max-w-2xl rounded-2xl"
        data-testid="onboarding-dialog"
        onEscapeKeyDown={() => closeNow()}
        onInteractOutside={() => closeNow()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="font-heading text-xl">Benvenuto! Personalizziamo insieme.</DialogTitle>
              <DialogDescription className="text-xs">In 30 secondi ti mostro solo quello che ti serve davvero.</DialogDescription>
            </div>
          </div>
          {/* Progress dots */}
          {totalSteps > 1 && (
            <div className="flex gap-2 mt-2" data-testid="onboarding-progress">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${i < step ? "bg-primary" : "bg-muted"}`} />
              ))}
            </div>
          )}
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3 mt-2" data-testid="onboarding-step-1">
            <p className="text-sm text-muted-foreground">Come guadagni?</p>
            <div className="grid grid-cols-1 gap-2">
              {[
                { key: "dipendente", icon: "👔", title: "Lavoratore dipendente", desc: "Ho una busta paga (indeterminato/determinato)" },
                { key: "piva", icon: "🧾", title: "Freelance / P.IVA", desc: "Ho aperto una partita IVA (forfettario o ordinario)" },
                { key: "entrambi", icon: "🎯", title: "Entrambi", desc: "Sono dipendente E ho una P.IVA collaterale" },
                { key: "nessuno", icon: "🧘", title: "Nessuno dei due (ancora)", desc: "Studente, disoccupato, casalinga, ecc." },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setWorkType(opt.key)}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${
                    workType === opt.key
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/30 hover:bg-muted/30"
                  }`}
                  data-testid={`onboarding-work-${opt.key}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">{opt.icon}</div>
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{opt.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{opt.desc}</div>
                    </div>
                    {workType === opt.key && <CheckCircle2 className="w-5 h-5 text-primary" />}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 mt-2" data-testid="onboarding-step-2">
            {isDipendente && (
              <div className="p-4 rounded-xl border bg-muted/30">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-4 h-4 text-primary" />
                  <span className="font-semibold text-sm">Il tuo lavoro dipendente</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs">RAL — Reddito Annuo Lordo (€)</Label>
                    <Input type="number" value={ral} onChange={(e) => setRal(e.target.value)} placeholder="Es. 30000" className="h-11 rounded-xl mt-1" data-testid="ob-ral" />
                    <p className="text-[11px] text-muted-foreground mt-1">Lordo annuo. Lo vedi sulla busta paga alla voce "RAL" o "totale lordo × 12".</p>
                  </div>
                  <div>
                    <Label className="text-xs">Mensilità</Label>
                    <Select value={String(mensilita)} onValueChange={(v) => setMensilita(Number(v))}>
                      <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="ob-mens"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="12">12</SelectItem><SelectItem value="13">13</SelectItem><SelectItem value="14">14</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
            {isPiva && (
              <div className="p-4 rounded-xl border bg-muted/30">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🧾</span>
                  <span className="font-semibold text-sm">La tua P.IVA</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Regime fiscale</Label>
                    <Select value={tipoPiva} onValueChange={setTipoPiva}>
                      <SelectTrigger className="h-11 rounded-xl mt-1" data-testid="ob-tipopiva"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="forfettario">Forfettario</SelectItem>
                        <SelectItem value="ordinario">Ordinario / Semplificato</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Codice ATECO (opzionale)</Label>
                    <Input value={ateco} onChange={(e) => setAteco(e.target.value)} placeholder="62.02.00" className="h-11 rounded-xl mt-1" data-testid="ob-ateco" />
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">Puoi aggiungerlo dopo — c'è un aiutante AI in Impostazioni se non lo ricordi.</p>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 mt-2" data-testid="onboarding-step-3">
            <div className="flex items-center gap-2">
              <Baby className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">Famiglia (per Assegno Unico e Bonus)</span>
            </div>
            <p className="text-xs text-muted-foreground">Aggiungi i tuoi figli a carico. Se non ne hai, salta pure questo step.</p>

            <div className="space-y-2">
              {figli.map((f, i) => (
                <div key={i} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-[10px] uppercase text-muted-foreground">Età figlio {i + 1}</Label>
                    <Input
                      type="number" min={0} max={24} value={f.eta}
                      onChange={(e) => setFigli(figli.map((x, idx) => idx === i ? { ...x, eta: e.target.value } : x))}
                      className="h-11 rounded-xl mt-1"
                      data-testid={`ob-figlio-${i}`}
                    />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setFigli(figli.filter((_, idx) => idx !== i))} className="h-11 w-11" data-testid={`ob-figlio-${i}-remove`}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={() => setFigli([...figli, { eta: 0 }])} className="rounded-full" data-testid="ob-add-figlio">
                <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi figlio
              </Button>
            </div>

            <div>
              <Label className="text-xs">ISEE (opzionale)</Label>
              <Input type="number" value={isee} onChange={(e) => setIsee(e.target.value)} placeholder="Es. 22000" className="h-11 rounded-xl mt-1" data-testid="ob-isee" />
              <p className="text-[11px] text-muted-foreground mt-1">Serve per Assegno Unico, Bonus Asilo, Bonus Energia. Puoi aggiungerlo dopo.</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t">
          <div>
            {step > 1 && (
              <Button variant="ghost" onClick={() => setStep(step - 1)} className="rounded-full" data-testid="ob-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step < totalSteps ? (
              <>
                <Button variant="ghost" onClick={() => finish(true)} className="rounded-full text-xs" data-testid="ob-skip">
                  Salta e completa dopo
                </Button>
                <Button onClick={next} className="rounded-full" data-testid="ob-next">
                  Avanti <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </>
            ) : (
              <Button onClick={() => finish(false)} className="rounded-full" data-testid="ob-finish">
                <>Fatto, iniziamo! <Sparkles className="w-4 h-4 ml-1" /></>
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
